import { SlideType } from '@llx/common';
import { isSlideQuestionSlide } from '@llx/common';
import { PatientCaseContent } from '@llx/common';
import { useMemo, useCallback, useRef, useEffect } from 'react';

import {
  AnswerFieldsFragment,
  AnswerRelationUserFragment,
  AnswerWhereInput,
  ContextType,
  useListAnswersWithUsersQuery,
  ListAnswersWithUsersDocument,
  ListAnswersWithUsersQuery,
  ListAnswersWithUsersQueryVariables,
  UserFieldsFragment,
  UserFieldsFragmentDoc,
  AnswerFieldsFragmentDoc,
} from '@/@generated/codegen/API';
import {
  LoggedType,
  useUserAuthContext,
} from '@/components/AuthTokenContextProvider/UserAuthContext';
import { useApolloClientContext } from '@/components/CustomApolloProvider/CustomApolloProvider';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import { captureSentry } from '@/utils/helpers/sentryHelper';

export type UseAnswersBySlideReturnType = {
  answers: (AnswerFieldsFragment & AnswerRelationUserFragment)[];
  userAnswers: (AnswerFieldsFragment & AnswerRelationUserFragment)[] | null;
  isLoading: boolean;
  slideID: string | undefined;
};

type Args = {
  contextType: ContextType;
  slideId: string | null;
  objId: string | null;
  patientCaseContent: PatientCaseContent | null | undefined;
  patientCaseId: string | null | undefined;
  isPreview?: boolean;
  mockedAnswers?: (AnswerFieldsFragment & AnswerRelationUserFragment)[];
};

export function useAnswersBySlide({
  contextType,
  slideId,
  objId,
  patientCaseContent,
  patientCaseId,
  isPreview = false,
  mockedAnswers = [],
}: Args): UseAnswersBySlideReturnType {
  const authContext = useUserAuthContext();
  const currentSlide = patientCaseContent?.slides.find((slide) => slide.id === slideId);
  const shouldSkip =
    isPreview ||
    !patientCaseContent ||
    !patientCaseId ||
    !currentSlide ||
    (!isSlideQuestionSlide(currentSlide) && currentSlide.type !== SlideType.CUSTOM);
  const { apolloClient } = useApolloClientContext();

  const where: AnswerWhereInput = useMemo(() => {
    const base: AnswerWhereInput = {
      slideId: {
        equals: slideId,
      },
    };
    switch (contextType) {
      case ContextType.Meeting:
        base.meetingId = {
          equals: objId,
        };
        break;
      case ContextType.Masterclass:
        base.masterClassUserSessionId = {
          equals: objId,
        };
        break;
      default: {
        const _exhaustiveCheck: never = contextType;
        console.error('Unhandled context kind in useAnswersBySlide:', contextType);
        throw new Error(`Unhandled context kind`);
      }
    }
    return base;
  }, [slideId, contextType, objId]);

  const queryVariables: ListAnswersWithUsersQueryVariables = useMemo(
    () => ({
      paginationArgs: {
        first: 100,
      },
      where,
      orderByArgs: {
        direction: 'desc',
        orderByField: 'createdAt',
      },
    }),
    [where]
  );

  const answersQuery = useListAnswersWithUsersQuery({
    variables: queryVariables,
    skip: shouldSkip,
    fetchPolicy: isPreview ? 'cache-only' : 'cache-and-network',
  });

  // BUGFIX: Buffer subscription updates while the initial query is loading to avoid
  // race conditions where subscription data gets overwritten by the query response.
  // After query completes, process buffered events that represent real-time changes.
  const pendingUpdatesRef = useRef<AnswerFieldsFragment[]>([]);

  const updateCacheWithAnswer = useCallback(
    (answer: AnswerFieldsFragment): void => {
      // Update the listAnswers cache for the same variables used in this hook
      const client = apolloClient;
      if (!client) return;

      type Edge = ListAnswersWithUsersQuery['listAnswers']['edges'][number];
      const makeEdge = (elem: AnswerFieldsFragment): Edge => {
        const user = client.cache.readFragment<UserFieldsFragment>({
          id: client.cache.identify({ __typename: 'User', id: elem.userId }),
          fragment: UserFieldsFragmentDoc,
        });

        if (!user) {
          captureSentry({
            title: 'Could not find User in cache when updating answers cache in useAnswersBySlide',
            detail: { answer: elem },
          });
          // NOTE: This should not happen because it will be in the cache because of the liveMeetingPariticpant subscription
          // but in case it does happen we return a fake user
          return {
            __typename: 'AnswerEdge',
            cursor: elem.id,
            node: {
              __typename: 'Answer',
              ...elem,
              user: {
                __typename: 'User',
                id: elem.userId,
                email: 'unknown@livelinx.com',
                isGuest: true,
                createdAt: new Date(),
                clientRole: '',
              },
            },
          };
        }
        return {
          __typename: 'AnswerEdge',
          cursor: elem.id,
          node: {
            __typename: 'Answer',
            ...elem,
            user,
          },
        };
      };

      // First, write the Answer entity to cache (this is atomic per entity)
      client.cache.writeFragment({
        id: client.cache.identify({ __typename: 'Answer', id: answer.id }),
        fragment: AnswerFieldsFragmentDoc,
        data: answer,
      });

      // Then use updateQuery with deduplication
      // NOTE: updateQuery is not atomic, but writing the fragment first ensures
      // the entity exists in cache, reducing the race window
      client.cache.updateQuery<ListAnswersWithUsersQuery, ListAnswersWithUsersQueryVariables>(
        { query: ListAnswersWithUsersDocument, variables: queryVariables },
        (data) => {
          if (!data) {
            const initValue: ListAnswersWithUsersQuery = {
              __typename: 'Query',
              listAnswers: {
                __typename: 'AnswerConnection',
                edges: [makeEdge(answer)],
                pageInfo: {
                  __typename: 'PageInfo',
                  hasNextPage: false,
                  endCursor: null,
                  hasPreviousPage: false,
                  startCursor: null,
                },
              },
            };
            return initValue;
          }

          // Create a Map for O(1) lookup and deduplication
          const edgeMap = new Map<string, Edge>();

          // Add existing edges to map (preserves order via insertion order)
          for (const edge of data.listAnswers.edges ?? []) {
            edgeMap.set(edge.node.id, edge);
          }

          // Handle the new/updated answer
          const existingEdge = edgeMap.get(answer.id);

          if (existingEdge) {
            // Update existing answer - merge with new data
            edgeMap.set(answer.id, makeEdge({ ...existingEdge.node, ...answer }));
          } else {
            // New answer - add it
            edgeMap.set(answer.id, makeEdge(answer));
          }

          // Convert back to array and sort
          const edges = Array.from(edgeMap.values());
          edges.sort(
            (a, b) =>
              new Date(String(b.node.createdAt)).getTime() -
              new Date(String(a.node.createdAt)).getTime()
          );

          // Cap to 100 items
          const limited = edges.slice(0, 100);

          const updated: ListAnswersWithUsersQuery = {
            ...data,
            listAnswers: {
              ...data.listAnswers,
              edges: limited,
            },
          };
          return updated;
        }
      );
    },
    [apolloClient, queryVariables]
  );

  const answerList = useMemo(() => {
    if (isPreview) {
      return mockedAnswers
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .filter((a) => a.slideId === slideId);
    }
    return answersQuery.data?.listAnswers.edges.map((edge) => edge.node) || [];
  }, [answersQuery.data, isPreview, mockedAnswers, slideId]);

  const userId = authContext.status === LoggedType.LOGGED_IN ? authContext.userID : null;

  const userAnswers = useMemo(() => {
    const filtered = answerList?.filter((answer) => answer.userId === userId);
    return filtered.length > 0 ? filtered : null;
  }, [answerList, userId]);

  useEntitySubscription('Answer', {
    onCreate: {
      variables: {
        where,
      },
      skip: shouldSkip,
      onData: (res) => {
        const data = res.data.data?.onCreateAnswer;
        if (!data) {
          captureSentry({
            title: 'useAnswersBySlide - onCreateAnswer subscription received no data',
            detail: { res },
          });
          return;
        }
        console.log('[useAnswersBySlide] New answer received', {
          userId: data.userId,
          slideId: data.slideId,
        });
        // Buffer updates while query is loading
        if (answersQuery.loading) {
          console.log('[useAnswersBySlide] Buffering onCreate update until initial query loads');
          pendingUpdatesRef.current.push(data);
          return;
        }
        updateCacheWithAnswer(data);
      },
    },
    onUpdate: {
      variables: {
        where,
      },
      skip: shouldSkip,
      onData: (res) => {
        const data = res.data.data?.onUpdateAnswer;
        if (!data) {
          captureSentry({
            title: 'useAnswersBySlide - onUpdateAnswer subscription received no data',
            detail: { res },
          });
          return;
        }
        console.log('[useAnswersBySlide] UPDATE answer received', {
          userId: data.userId,
          slideId: data.slideId,
        });
        // Buffer updates while query is loading
        if (answersQuery.loading) {
          console.log('[useAnswersBySlide] Buffering onUpdate update until initial query loads');
          pendingUpdatesRef.current.push(data);
          return;
        }
        updateCacheWithAnswer(data);
      },
    },
    onDelete: {
      variables: {},
      skip: shouldSkip,
      onData: (res) => {
        const obj = res.data.data?.onDeleteAnswer;
        if (!obj) {
          captureSentry({
            title: 'useAnswersBySlide | onDelete | onData | obj is undefined',
            detail: {},
          });
          return;
        }
        if (obj.slideId !== slideId) return;
        // NOTE: Deleting an answer is not possible right now, so we just refetch the list
        void answersQuery.refetch();
      },
    },
  });

  // Process any buffered subscription updates after the initial query completes
  useEffect(() => {
    if (!answersQuery.loading && pendingUpdatesRef.current.length > 0) {
      console.log(
        '[useAnswersBySlide] Processing buffered updates:',
        pendingUpdatesRef.current.length
      );
      const updates = pendingUpdatesRef.current;
      pendingUpdatesRef.current = [];
      updates.forEach((answer) => updateCacheWithAnswer(answer));
    }
  }, [answersQuery.loading, updateCacheWithAnswer]);

  const result = useMemo(() => {
    const res = {
      slideID: currentSlide?.id,
      isLoading: answersQuery.loading,
      answers: answerList,
      userAnswers: userAnswers,
    };
    console.log('[useAnswersBySlide] Result:', res);
    return res;
  }, [currentSlide?.id, answersQuery.loading, answerList, userAnswers]);

  return result;
}
